import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, ObjUtil, Time, Util} from 'yougile-util'
import {
  DataObjectId,
  KeyReq,
  KeyResp,
  PhoneVerificationResp,
  TwoFactorStep
} from 'yougile-model'
import {storage2FaKeys} from 'yougile-model'
import {Storage} from '@client/storage'
import {Backend} from '@common/backend'
import {
  addGoogleSignIn,
  approveSignInCode,
  getGoogleClientId,
  resetGoogleSignInState
} from '@common/google-sign-in'
import {showAlert} from '@components/common/base/notifications'
import {createUseStore, keyboardHandler} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {Refresh} from '../../client/refresh'
import {companyLoginStore} from '../company-login/company-login-store'
import type {Company} from '../company-login/types'
import {LoginHelper} from './login-helper'

interface State {
  isDesktop: boolean
  copy: string
  code: string

  company?: Company
  twoFactorStep?: TwoFactorStep
  isPhoneSetByUser?: boolean
  userId?: DataObjectId
  authKey?: string
  phoneIsInvalid?: boolean
  phoneToVerify?: string
  isPendingTwoFactor?: boolean
  twoFactorCodeSentTo?: string
  retryTimestamp?: number
  codeError?: string
  phoneError?: string

  loginFormEmail: string
  loginFormPassword: string
  loginFormError: string
  loginFormWait: boolean
  loginFormRemember: boolean

  forgetShow: boolean
  forgetEmail: string
  forgetWait: boolean
  forgetError: string
  forgetSuccess: boolean

  // for entering custom server url (desktop version)
  serverSavedValue: string
  serverShow: boolean
  serverValue: string
  serverError: string
}

export class LoginStore {
  state: State

  constructor({company}: {company?: Company}) {
    this.state = {
      code: Util.randomAlphanum(32),
      isDesktop: Util.isDesktop(),
      copy: 'YouGile © ' + new Date(Time.now()).getFullYear(),
      company,

      loginFormEmail: Backend.loadLocal('desktop-cred1') || '',
      loginFormPassword: Backend.loadLocal('desktop-cred2') || '',
      loginFormError: '',
      loginFormWait: false,
      loginFormRemember: !!Backend.loadLocal('desktop-cred1'),

      forgetShow: false,
      forgetEmail: '',
      forgetWait: false,
      forgetError: '',
      forgetSuccess: false,

      serverSavedValue: Backend.loadLocal('server_url'),
      serverShow: false,
      serverValue: Backend.loadLocal('server_url'),
      serverError: ''
    }

    this.sync2faFromStorage()

    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )

    window.addEventListener('message', (e: MessageEvent) => {
      if (e.data === 'google-signin') {
        void approveSignInCode(this.state.code, showAlert)
      }
    })
  }

  sync2faFromStorage(): void {
    const storageData = LoginHelper.get2FaDataFromStorage()

    if (ObjUtil.keys(storageData).length) {
      this.state = {...this.state, ...storageData}
    }
  }

  isDesktopWithSocialButtons(): boolean {
    return this.state.isDesktop && !!getGoogleClientId(this.getServerHostname())
  }

  setPhoneIsInvalid(value: boolean): void {
    this.state.phoneIsInvalid = value
  }

  private getServerHostname(): string {
    return (this.state.serverSavedValue || 'https://yougile.com')
      .split('//')
      .pop()
      .split(':')[0]
  }

  renderDesktopSocialButton(el?: HTMLElement): void {
    if (!el) {
      return
    }
    el.innerHTML = ''
    const frame = document.createElement('iframe')
    frame.classList.add('login__google-frame')
    const serverUrl = this.state.serverSavedValue || 'https://yougile.com'
    frame.src = `${serverUrl}/google-sign-in-gateway.html?code=${
      this.state.code
    }&lang=${Lang.getLang()}`
    el.appendChild(frame)
  }

  isSocialButtonsAvailable(): boolean {
    return (
      !globals.standalone && !this.state.isDesktop && addGoogleSignIn(showAlert)
    )
  }

  initLoginSocials(el: HTMLElement): void {
    if (!el) {
      return
    }
    if (this.isSocialButtonsAvailable()) {
      el.classList.remove('hidden')
    }
  }

  goToMainSite(e: React.MouseEvent): void {
    e.preventDefault()
    location.href = '/'
  }

  toggleRemember(): void {
    this.state.loginFormRemember = !this.state.loginFormRemember
    Backend.saveLocal(
      'desktop-remember',
      this.state.loginFormRemember ? '1' : '0'
    )
  }

  keyDown = keyboardHandler({
    enter: () => this.submit(),
    anyKey: () => {
      this.state.loginFormError = ''
    }
  })

  setTwoFactorStep(value: TwoFactorStep | undefined): void {
    if (value) {
      Storage.setItem(storage2FaKeys.step, value)
    } else {
      Storage.removeItem(storage2FaKeys.step)
    }
    this.state.twoFactorStep = value
  }

  closeTwoFactorStep(): void {
    resetGoogleSignInState()
    this.state.loginFormPassword = ''
    this.state.loginFormEmail = ''
    this.state.isPhoneSetByUser = false
    this.state.phoneError = undefined
    this.state.phoneIsInvalid = undefined
    this.state.codeError = undefined
    this.setTwoFactorStep(undefined)
    LoginHelper.clear2faFromStorage()
  }

  setPendingTwoFactor(value: boolean): void {
    this.state.isPendingTwoFactor = value
  }

  async startPhoneVerification(phone: string): Promise<void> {
    if (!phone || !this.state.userId) {
      this.closeTwoFactorStep()
      return
    }

    this.setPendingTwoFactor(true)

    await Backend.postJSON<PhoneVerificationResp>(
      '/data/two-factor/save-user-auth-phone',
      {
        phone: phone.replace(/\D/g, ''),
        password: this.state.loginFormPassword,
        userId: this.state.userId,
        lang: Lang.getLang()
      }
    )
      .then(response => {
        this.state.userId = response.userId
        this.state.twoFactorCodeSentTo = response.codeSentTo
        this.state.retryTimestamp = response.retryTimestamp
        this.state.isPhoneSetByUser = true
        Storage.setItem(storage2FaKeys.isPhoneSetByUser, '1')

        this.setTwoFactorStep(response.nextStep)
        if (response.codeSentTo) {
          Storage.setItem(storage2FaKeys.codeSentTo, response.codeSentTo)
        }
        if (response.retryTimestamp) {
          Storage.setItem(
            storage2FaKeys.retryTimestamp,
            String(response.retryTimestamp)
          )
        }

        if (response.error) {
          this.setPhoneError(response.error)
        }
      })
      .catch(error => {
        if (error?.code === 422) {
          this.setTwoFactorStep('blocked')
        }
        if (error.message) {
          this.setPhoneError(error.message)
        }
      })
      .finally(() => {
        this.setPendingTwoFactor(false)
      })
  }

  async submitPhoneVerificationCode(code: string): Promise<void> {
    if (!code || !this.state.userId || !this.state.loginFormPassword) {
      this.setTwoFactorStep(undefined)
      return
    }

    this.setPendingTwoFactor(true)

    try {
      const res = await Backend.postJSON<KeyResp>('/data/key', {
        smsCode: code,
        email: this.state.loginFormEmail,
        password: this.state.loginFormPassword,
        userId: this.state.userId,
        lang: Lang.getLang()
      })
      this.state.userId = res.userId
      this.state.authKey = res.key

      LoginHelper.set2FaDataToStorage({
        twoFactorStep: this.state.isPhoneSetByUser ? 'success' : undefined
      })

      if (this.state.isPhoneSetByUser) {
        this.setTwoFactorStep('success')
        return
      }
      await this.saveCredsAndRefresh()
    } catch (err) {
      logger.warn(err)
      if (err.message === Lang.msg.users.twoFactorBlocked) {
        this.setTwoFactorStep('blocked')
        return
      }
      if (err.message) {
        this.setCodeError(
          err.message.toLowerCase() === 'network error'
            ? Lang.msg.noConnectionShort
            : err.message
        )
      }
    } finally {
      this.setPendingTwoFactor(false)
    }
  }

  setCodeError(value?: string): void {
    this.state.codeError = value
  }

  setPhoneError(value?: string): void {
    this.state.phoneError = value
    if (value) {
      this.setPhoneIsInvalid(true)
    }
  }

  companyBack(): void {
    resetGoogleSignInState()
    companyLoginStore.setScreen('main')
  }

  async saveCredsAndRefresh(): Promise<void> {
    if (this.state.userId && this.state.authKey) {
      Backend.saveCredentials(this.state.userId, this.state.authKey)
    }
    LoginHelper.clear2faFromStorage()
    if (this.state.company?.id) {
      const err = await companyLoginStore.enterCurrent(
        this.state.company.id,
        false
      )
      err && (this.state.loginFormError = err)
      return
    }
    // hack for FF (to ask save password)
    const el = document.querySelector<HTMLFormElement>('.login-form')
    el?.submit?.()
    Refresh.refresh()
  }

  async submit({resendCode}: {resendCode?: boolean} = {}): Promise<void> {
    if (this.state.loginFormWait) {
      return
    }
    if (!this.state.loginFormEmail) {
      this.state.loginFormError = Lang.msg.incorrectEmailPass
      return
    }
    this.state.loginFormWait = true
    const keyReq: KeyReq = {
      email: this.state.loginFormEmail,
      password: this.state.loginFormPassword,
      resendCode: resendCode
    }

    if (this.state.retryTimestamp) {
      this.state.retryTimestamp = undefined
    }

    try {
      const res = await Backend.postJSON<KeyResp>('/data/key', keyReq)

      this.state.userId = res.userId
      this.state.authKey = res.key
      if (res.nextStep) {
        if (!res.userId) {
          return
        }
        this.state.twoFactorCodeSentTo = res.codeSentTo
        this.state.retryTimestamp = res.retryTimestamp

        LoginHelper.set2FaDataToStorage({
          twoFactorStep: res.nextStep,
          loginFormEmail: this.state.loginFormEmail,
          loginFormPassword: this.state.loginFormPassword,
          userId: this.state.userId,
          authKey: this.state.authKey,
          retryTimestamp: this.state.retryTimestamp,
          twoFactorCodeSentTo: this.state.twoFactorCodeSentTo,
          isPhoneSetByUser: this.state.isPhoneSetByUser
        })

        this.setTwoFactorStep(res.nextStep)
        return
      }
      if (this.state.isDesktop && this.state.loginFormRemember) {
        Backend.saveLocal('desktop-cred1', this.state.loginFormEmail)
        Backend.saveLocal('desktop-cred2', this.state.loginFormPassword)
      } else {
        Backend.removeLocal('desktop-cred1')
        Backend.removeLocal('desktop-cred2')
      }
      await this.saveCredsAndRefresh()
    } catch (err) {
      const networkError = err.message || Lang.msg.noConnectionShort

      if (networkError === Lang.msg.users.twoFactorBlocked) {
        this.setTwoFactorStep('blocked')
        return
      }

      this.state.loginFormError =
        networkError === 'Network error'
          ? Lang.msg.noConnectionShort
          : networkError
      if (this.state.twoFactorStep === 'phone') {
        this.setPhoneError(networkError)
      }
      if (this.state.twoFactorStep === 'code') {
        this.setCodeError(networkError)
      }
    } finally {
      this.state.loginFormWait = false
    }
  }

  forgetSubmit(): void {
    if (!Util.isEmail(this.state.forgetEmail)) {
      this.state.forgetError = Lang.msg.emailIncorrect
      return
    }
    this.state.forgetWait = true
    void Backend.postJSON('/data/recovery-request', {
      email: this.state.forgetEmail
    })
      .then(() => (this.state.forgetSuccess = true))
      .catch(error => {
        logger.error('Error on recovery request', error)
        this.state.forgetError = Lang.msg.serverError
      })
      .then(() => {
        runInAction(() => (this.state.forgetWait = false))
      })
  }

  forgetKeyDown(e: React.KeyboardEvent): void {
    this.state.forgetSuccess = false
    this.state.forgetError = ''
    if (e.keyCode === 13) {
      this.forgetSubmit()
      e.preventDefault()
    }
  }

  private reloadServer(): void {
    void Backend.init()
      .then(() => {
        this.state.serverSavedValue = this.state.serverValue
        this.state.serverShow = false
        this.state.serverError = ''
        if (this.isDesktopWithSocialButtons()) {
          this.renderDesktopSocialButton()
        }
      })
      .catch(() => {
        Backend.removeLocal('server_url')
        this.state.serverError = 'Server is not responding'
      })
  }

  serverClear(): void {
    Backend.removeLocal('server_url')
    this.state.serverSavedValue = ''
    this.state.serverValue = ''
    this.state.serverError = ''
    this.reloadServer()
  }

  serverSubmit(): void {
    if (!this.state.serverValue) {
      this.state.serverError = 'Incorrect server address'
      return
    }
    Backend.saveLocal('server_url', this.state.serverValue)
    this.reloadServer()
  }

  serverKeyDown(e: React.KeyboardEvent): void {
    this.state.serverError = ''
    if (e.keyCode === 13) {
      this.serverSubmit()
      e.preventDefault()
    }
  }

  setLoginFormEmail(e: React.ChangeEvent<HTMLInputElement>): void {
    this.state.loginFormEmail = (e.target as HTMLInputElement).value
  }

  setLoginFormPassword(e: React.ChangeEvent<HTMLInputElement>): void {
    this.state.loginFormPassword = (e.target as HTMLInputElement).value
  }

  setForgetShow(value: boolean): void {
    this.state.forgetShow = value
  }

  setForgetEmail(e: React.ChangeEvent<HTMLInputElement>): void {
    this.state.forgetEmail = (e.target as HTMLInputElement).value
  }

  setServerShow(value: boolean): void {
    this.state.serverShow = value
  }

  setServerValue(e: React.ChangeEvent<HTMLInputElement>): void {
    this.state.serverValue = (e.target as HTMLInputElement).value
  }
}

export const [LoginContext, useLoginStore] = createUseStore(LoginStore)
